import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Nav, Navbar, NavDropdown } from "react-bootstrap";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { RiHeartLine, RiCalendarLine, RiBookLine, RiStarLine } from "react-icons/ri";
import { logout } from "../../redux/actions/authActions";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { RootState } from "../../redux/store";
import { getLogoPath } from "../../utils/logoHelper";
import { isBrainBodyMSK } from "../../utils/brandingConfig";
import { PowerIcon } from "../icons";
import { LanguageSelector } from "../language-selector";
import { isMHIPAvailable } from "../../routes/dashboard/helpers";
import "./navbar-new-ui.css";

function NavbarNewUI() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const logoSrc = getLogoPath();
  const isBrainBody = isBrainBodyMSK();
  const history = useHistory();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const isExercisePage = location.pathname.startsWith("/exercises/");

  useEffect(() => {
    if (isExercisePage) return undefined;
    document.body.classList.add("has-bottom-nav");
    return () => document.body.classList.remove("has-bottom-nav");
  }, [isExercisePage]);

  const closeMenu = () => setExpanded(false);

  const onLogoutClicked = async () => {
    setExpanded(false);
    await dispatch(logout());
    history.push("/login");
  };

  const isAdmin = currentUser?.["custom:isAdmin"] === "true";
  const initials =
    currentUser?.["given_name"] && currentUser?.["family_name"] ? `${currentUser["given_name"][0]}${currentUser["family_name"][0]}` : "";
  const fullName =
    currentUser?.["given_name"] && currentUser?.["family_name"] ? `${currentUser["given_name"]} ${currentUser["family_name"]}` : "";

  const showLibrary = !isAdmin && isMHIPAvailable(currentUser?.summary);

  const brandHref = isAdmin ? "/admin" : "/";

  const avatarDropdownTitle = (
    <span className="new-ui-navbar__avatar-trigger">
      <span className="new-ui-navbar__avatar">{initials}</span>
      <span className="new-ui-navbar__user-info">
        <span className="new-ui-navbar__user-name">{fullName}</span>
        {isAdmin && <span className="new-ui-navbar__role">{t("navbar.admin")}</span>}
      </span>
    </span>
  );

  const bottomNavTabs = [
    { to: "/", exact: true, icon: <RiHeartLine aria-hidden="true" />, label: t("navbar.current") },
    { to: "/past-programs", exact: true, icon: <RiCalendarLine aria-hidden="true" />, label: t("navbar.past") },
    ...(showLibrary ? [{ to: "/library", exact: true, icon: <RiBookLine aria-hidden="true" />, label: t("navbar.library") }] : []),
    { to: "/favorites", exact: true, icon: <RiStarLine aria-hidden="true" />, label: t("navbar.favorites") },
  ];

  return (
    <>
      <Navbar
        className={`new-ui-navbar${isExercisePage ? " new-ui-navbar--exercise-page" : ""}`}
        expand={false}
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Navbar.Brand
          href={brandHref}
          aria-label="Go to home page"
          className="new-ui-navbar__brand"
          style={isBrainBody ? { marginLeft: "1em" } : {}}
        >
          <img
            alt=""
            aria-hidden="true"
            src={logoSrc}
            height="30"
            className="d-inline-block align-top"
            style={
              isBrainBody ? { maxWidth: "160px", maxHeight: "30px", width: "auto", height: "auto", display: "block", marginTop: "5px" } : {}
            }
          />
        </Navbar.Brand>

        <div className="new-ui-navbar__mobile-actions">
          <Navbar.Toggle aria-controls="new-ui-navbar-nav" className="new-ui-navbar__mobile-toggle">
            <span className="new-ui-navbar__avatar">{initials}</span>
          </Navbar.Toggle>
        </div>

        {expanded && <div className="new-ui-navbar__backdrop" onClick={closeMenu} aria-hidden="true" />}

        <Navbar.Collapse id="new-ui-navbar-nav">
          <Nav className="new-ui-navbar__nav" onClick={closeMenu}>
            {isAdmin ? (
              <>
                <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/admin/messenger">
                  {t("navbar.messenger")}
                </Nav.Link>
                <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/admin/programs">
                  {t("navbar.programs")}
                </Nav.Link>
                <Nav.Link
                  as={NavLink}
                  className="new-ui-navbar__link"
                  activeClassName="new-ui-navbar__link--active"
                  to="/admin/surveys"
                  exact
                >
                  {t("navbar.surveys")}
                </Nav.Link>
                <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/admin" exact>
                  {t("navbar.members")}
                </Nav.Link>
              </>
            ) : (
              <>
                <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/" exact>
                  <span className="new-ui-navbar__link-content">
                    <RiHeartLine aria-hidden="true" />
                    {t("navbar.current_program")}
                  </span>
                </Nav.Link>
                <Nav.Link
                  as={NavLink}
                  className="new-ui-navbar__link"
                  activeClassName="new-ui-navbar__link--active"
                  to="/past-programs"
                  exact
                >
                  <span className="new-ui-navbar__link-content">
                    <RiCalendarLine aria-hidden="true" />
                    {t("navbar.past_program")}
                  </span>
                </Nav.Link>
                {showLibrary && (
                  <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/library" exact>
                    <span className="new-ui-navbar__link-content">
                      <RiBookLine aria-hidden="true" />
                      {t("navbar.library")}
                    </span>
                  </Nav.Link>
                )}
                <Nav.Link as={NavLink} className="new-ui-navbar__link" activeClassName="new-ui-navbar__link--active" to="/favorites" exact>
                  <span className="new-ui-navbar__link-content">
                    <RiStarLine aria-hidden="true" />
                    {t("navbar.favorites")}
                  </span>
                </Nav.Link>
              </>
            )}
          </Nav>

          {/* Mobile extras: language + logout */}
          <Nav className="new-ui-navbar__mobile-extras">
            <LanguageSelector vertical smallSize className="new-ui-navbar__language" />
            <Nav.Link className="new-ui-navbar__mobile-logout" onClick={() => onLogoutClicked()}>
              <PowerIcon />
              <span>{t("navbar.log_out")}</span>
            </Nav.Link>
          </Nav>

          {/* Desktop: avatar dropdown */}
          <NavDropdown id="new-ui-avatar-dropdown" className="new-ui-navbar__avatar-dropdown" title={avatarDropdownTitle}>
            <NavDropdown.Item className="new-ui-navbar__logout-item" onClick={() => onLogoutClicked()}>
              <PowerIcon />
              <span>{t("navbar.log_out")}</span>
            </NavDropdown.Item>
          </NavDropdown>
        </Navbar.Collapse>
      </Navbar>

      {!isAdmin && (
        <nav
          className={`new-ui-bottom-nav${isExercisePage ? " new-ui-bottom-nav--exercise-page" : ""}`}
          aria-label={t("navbar.mobile_navigation")}
        >
          {bottomNavTabs.map(tab => (
            <NavLink
              key={tab.to}
              to={tab.to}
              exact={tab.exact}
              className="new-ui-bottom-nav__tab"
              activeClassName="new-ui-bottom-nav__tab--active"
            >
              <span className="new-ui-bottom-nav__icon">{tab.icon}</span>
              <span className="new-ui-bottom-nav__label">{tab.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}

export default NavbarNewUI;
